import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, errorMessage, get } from "@/lib/api";
import { S } from "@/lib/strings";

/**
 * Default poll interval: 60 s.
 *
 * The fork defaulted to 10 s. With eight widgets on a page and three operators
 * watching, that is 144 aggregate queries a minute against a shared 2-vCPU box
 * for data the console socket already pushes. Polling here is the *safety net*
 * for a missed frame, not the transport — so it is slow on purpose. Anything
 * genuinely live subscribes to a topic instead.
 */
export const DEFAULT_REFETCH_MS = 60_000;

export function useApiQuery<T>(
  key: QueryKey,
  path: string,
  opts?: Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => get<T>(path),
    refetchInterval: DEFAULT_REFETCH_MS,
    ...opts,
  });
}

/**
 * A mutation whose failure lands as a sonner toast carrying the *server's*
 * message plus a 重試 action. A generic "Something went wrong" throws away the
 * one piece of information the operator needs.
 */
export function useApiMutation<TData, TVars>(
  fn: (vars: TVars) => Promise<TData>,
  opts?: Omit<UseMutationOptions<TData, ApiError, TVars>, "mutationFn"> & {
    /** Query keys to invalidate on success. */
    invalidates?: QueryKey[];
    /** Toast shown on success; omit for silent mutations. */
    successMessage?: string | ((data: TData, vars: TVars) => string);
    /** Prefix for the error toast, e.g. "派發失敗". */
    errorTitle?: string;
  },
) {
  const qc = useQueryClient();
  const { invalidates, successMessage, errorTitle, ...rest } = opts ?? {};

  return useMutation<TData, ApiError, TVars>({
    mutationFn: fn,
    ...rest,
    // The callback arity moves between react-query minors; forward whatever it
    // hands us rather than pinning a shape that breaks on the next bump.
    onSuccess: (...args: Parameters<NonNullable<typeof rest.onSuccess>>) => {
      const [data, vars] = args;
      if (invalidates) for (const k of invalidates) void qc.invalidateQueries({ queryKey: k });
      if (successMessage) {
        toast.success(
          typeof successMessage === "function" ? successMessage(data, vars) : successMessage,
        );
      }
      rest.onSuccess?.(...args);
    },
    onError: (...args: Parameters<NonNullable<typeof rest.onError>>) => {
      const [err, vars] = args;
      toast.error(errorTitle ?? S.mutationFailed, {
        description: errorMessage(err),
        action: { label: S.retry, onClick: () => void mutationRetry(fn, vars) },
      });
      rest.onError?.(...args);
    },
  });
}

// The toast's 重試 fires the same call again without going back through the
// mutation's lifecycle — it is a convenience, and a second failure toasts again.
async function mutationRetry<TData, TVars>(fn: (v: TVars) => Promise<TData>, vars: TVars) {
  try {
    await fn(vars);
    toast.success(S.done);
  } catch (e) {
    toast.error(errorMessage(e));
  }
}
